import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MembershipModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/signup/early-bird`, {
        ...formData,
        language,
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', phone: '' });
        onClose();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(language === 'de' 
        ? 'Ein Fehler ist aufgetreten.' 
        : 'An error occurred.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Backdrop */}
          <motion.div 
            className="absolute inset-0 bg-black/95 backdrop-blur-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="modal-backdrop"
          />
          
          {/* Modal Content */}
          <motion.div 
            className="relative z-10 w-full max-w-lg bg-[#0a0a0a] border border-white/10 p-10 md:p-14"
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            data-testid="membership-modal"
          >
            {/* Glow Effect */}
            <div className="absolute -inset-px bg-gradient-to-b from-[#2bfecd]/10 to-transparent opacity-50 pointer-events-none" />
            
            {/* Close Button */}
            <motion.button 
              onClick={onClose}
              className="absolute top-6 right-6 text-white/30 hover:text-[#2bfecd] transition-colors duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              data-testid="modal-close-button"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </motion.button>
            
            {/* Header */}
            <div className="text-center mb-12">
              <motion.h3 
                className="font-cinzel text-4xl tracking-[0.25em] uppercase text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                ICEBACH
              </motion.h3>
              
              <motion.div 
                className="flex items-center justify-center gap-3 mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="w-10 h-px bg-gradient-to-r from-transparent to-[#2bfecd]/40" />
                <div className="w-2 h-2 rotate-45 border border-[#2bfecd]/40" />
                <div className="w-10 h-px bg-gradient-to-l from-transparent to-[#2bfecd]/40" />
              </motion.div>
              
              <motion.p 
                className="text-[10px] tracking-[0.4em] uppercase text-[#2bfecd]/70"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {t('membership.subtitle')}
              </motion.p>
            </div>
            
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {[
                { name: 'name', type: 'text', placeholder: t('membership.form.name'), required: true },
                { name: 'email', type: 'email', placeholder: t('membership.form.email'), required: true },
                { name: 'phone', type: 'tel', placeholder: t('membership.form.phone'), required: false },
              ].map((field, index) => (
                <motion.div
                  key={field.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <input 
                    type={field.type}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                    className="w-full bg-white/[0.03] border border-white/10 text-white px-5 py-4 text-sm tracking-wider placeholder:text-white/30 focus:outline-none focus:border-[#2bfecd]/50 transition-colors duration-300"
                    data-testid={`input-${field.name}`}
                  />
                </motion.div>
              ))}
              
              <motion.button 
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-8 group relative px-8 py-5 border border-[#2bfecd]/30 text-[#2bfecd] text-xs tracking-[0.25em] uppercase font-cinzel overflow-hidden transition-all duration-700 hover:border-[#2bfecd] disabled:opacity-50 disabled:cursor-not-allowed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.99 }}
                data-testid="submit-button"
              >
                <span className="relative z-10">
                  {isSubmitting ? t('membership.form.submitting') : t('membership.form.submit')}
                </span>
                <div className="absolute inset-0 bg-[#2bfecd] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700" />
                <span className="absolute inset-0 flex items-center justify-center text-black opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100 z-20 tracking-[0.25em]">
                  {isSubmitting ? t('membership.form.submitting') : t('membership.form.submit')}
                </span>
              </motion.button>
            </form>
            
            {/* Footer */}
            <motion.p 
              className="text-[9px] tracking-[0.25em] uppercase text-white/20 text-center mt-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              {t('membership.spots')}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MembershipModal;
